<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useQuery } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import {
  DownloadMbomDialogDataQuery,
  DownloadMbomDialogDataQueryVariables,
} from '@/generated/graphql'
import { computed } from 'vue'
import DownloadModuleMbomButton from '@/components/mbom/DownloadModuleMbomButton.vue'
import DownloadModuleWorkInstructionsButton from '@/components/mbom/DownloadModuleWorkInstructionsButton.vue'

const model = defineModel<boolean>({ required: true })
defineEmits<{
  'update:modelValue': [value: boolean]
}>()
const props = defineProps<{
  bopId: string
  type: 'mbom' | 'work-instructions'
}>()

const { t } = useI18n()

const fetchQuery = useQuery<DownloadMbomDialogDataQuery, DownloadMbomDialogDataQueryVariables>(
  gql`
    query DownloadMbomDialogData($bopId: ID!) {
      product {
        bop(id: $bopId) {
          nodes {
            id
            module {
              id
              name
              abbreviation
              description
            }
          }
        }
      }
    }
  `,
  () => ({
    bopId: props.bopId,
  }),
)

const modules = computed(() => {
  const seen = new Set<string>()
  return (
    fetchQuery.result.value?.product.bop.nodes
      .map((n) => n.module)
      .filter((m) => {
        if (seen.has(m.id)) return false
        seen.add(m.id)
        return true
      }) || []
  )
})

const title = computed(() =>
  props.type === 'mbom'
    ? t('component.downloadMbomDialog.title')
    : t('component.downloadWorkInstructionsDialog.title'),
)
</script>

<template>
  <v-dialog v-model="model" max-width="650" scrollable>
    <v-card>
      <v-card-title>{{ title }}</v-card-title>
      <v-card-text>
        <v-list class="text-center">
          <v-list-item v-for="m in modules" :key="m.id">
            <download-module-mbom-button
              v-if="props.type === 'mbom'"
              :bop-id="props.bopId"
              :module="m"
              variant="outlined"
              class="download-button"
            />
            <download-module-work-instructions-button
              v-else
              :bop-id="props.bopId"
              :module="m"
              variant="outlined"
              class="download-button"
            />
          </v-list-item>
        </v-list>
      </v-card-text>
      <v-card-actions>
        <v-btn variant="elevated" color="primary" @click="$emit('update:modelValue', false)">
          {{ t('component.downloadMbomDialog.closeButton') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped lang="scss">
.download-button {
  width: 100%;
  overflow: hidden;
}
</style>
